// eslint-disable-next-line no-restricted-imports
import type {Component, PanelLifecycleContext} from 'panel';
import type {i18n} from 'i18next';

import type {Subscribable} from 'common/types/context/subscribable';

import type {Translatable} from './index';

export class LocalizationStore implements PanelLifecycleContext, Subscribable {
  private connectedComponents = new Set<Component<any>>();
  private subscriptions = new Set<() => void>();

  constructor(private i18next: i18n) {
    i18next.on(`languageChanged`, this.updateComponents);
  }

  get language() {
    return this.i18next.language;
  }

  /** Gets translation for a key with options. */
  get t() {
    return this.i18next.t.bind(this.i18next);
  }

  /** Maybe translates a string if it's a function. Otherwise, returns the original string. */
  print = (translatable: Translatable): string => {
    return typeof translatable === `function` ? translatable(this.t) : translatable;
  };

  /**
   * Joins a list of translatables using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat/ListFormat#options
   */
  formatList = (
    items: Translatable[],
    type?: Intl.ListFormatOptions['type'],
    style?: Intl.ListFormatOptions['style'],
  ) => {
    const translatedItems = items.map(this.print);

    return new Intl.ListFormat(this.language, {type, style}).format(translatedItems);
  };

  public subscribe(callback: () => void): this {
    this.subscriptions.add(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions.delete(callback);
    return this;
  }

  public bindToComponent(component: Component<any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents = () => {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  };
}
